/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { addDays, differenceInDays, isAfter, isValid } from 'date-fns';
import { parseAbsolute, parseDate, now } from '@internationalized/date';
import { SecurityHelper } from 'sarsaparilla';
import { distance } from '@turf/distance';
import { point } from 'shared-ui/maps/turfUtils';
import { decodeHTML } from './stringUtils';

export function isTripEditable(trip) {
    const accountId = SecurityHelper.getAccount().account_id;
    return (
        trip &&
        trip.trip_id &&
        (!trip.account_id ||
            trip.account_id === accountId ||
            trip.account_id === 'system')
    );
}

export function isTripValid(trip) {
    let valid = true;
    trip.destinations.map((destination) => {
        if (!destination.lat || !destination.lng) {
            valid = false;
        }
        return null;
    });
    return valid;
}

export function metersToMiles(meters) {
    let miles = null;
    if (meters) {
        miles = (meters * 0.000621371).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    return miles;
}

export function milesToKms(miles) {
    let kms = null;
    if (typeof miles === 'number') {
        kms = (miles * 1.60934).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }
    return kms;
}

export function kmsToMiles(kms) {
    let miles = null;
    if (typeof kms === 'number') {
        miles = (kms * 0.621371).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    return miles;
}

export function sortByDistance(destinations) {
    if (!destinations || destinations.length <= 2) {
        return destinations;
    }

    const points = [...destinations];

    // keep first point static, as it is the trip starting point
    const sorted = [points[0]];
    const remaining = points.slice(1);

    while (remaining.length > 0) {
        const currentPoint = sorted[sorted.length - 1];
        let nearestIndex = 0;

        const from = point([currentPoint.lng, currentPoint.lat]);
        const toFirst = point([remaining[0].lng, remaining[0].lat]);
        let minDistance = distance(from, toFirst, { units: 'miles' });
        for (let i = 1; i < remaining.length; i += 1) {
            const toNext = point([remaining[i].lng, remaining[i].lat]);
            const dist = distance(from, toNext, { units: 'miles' });
            if (dist < minDistance) {
                minDistance = dist;
                nearestIndex = i;
            }
        }

        sorted.push(remaining[nearestIndex]);
        remaining.splice(nearestIndex, 1);
    }
    return sorted;
}

export function getDestinationIndexForDate(trip, date) {
    let startDate = null;
    if (trip.start_date && trip.start_date !== '') {
        startDate = trip.start_date;
    } else {
        startDate = new Date();
    }
    let ret = -1;
    for (let index = 0; index < trip.destinations.length; index++) {
        if (index > 0) {
            const destination = trip.destinations[index];
            if (destination.duration) {
                startDate = addDays(startDate, destination.duration);
                const diff = differenceInDays(startDate, date);
                if (diff > 0) {
                    ret = index;
                    break;
                }
            }
        }
    }
    if (ret === -1) {
        ret = trip.destinations.length - 1;
    }
    return ret;
}

export function getDateForDestinationIndex(trip, destinationIndex) {
    let startDate = null;
    if (trip.start_date && trip.start_date !== '') {
        startDate = trip.start_date;
    } else {
        startDate = new Date();
    }
    let ret = startDate;
    for (let index = 0; index < trip.destinations.length; index++) {
        if (index > 0) {
            const destination = trip.destinations[index];
            if (index === destinationIndex) {
                ret = startDate;
                break;
            }
            if (destination.duration) {
                startDate = addDays(startDate, destination.duration);
            }
        }
    }
    return ret;
}

export function getSearchDocId(tripItem) {
    let id;
    if (tripItem.entity_id) {
        if (
            tripItem.entity_type === 'tour' ||
            tripItem.entity_type === 'ticketfacility' ||
            tripItem.entity_type === 'permit' ||
            tripItem.entity_type === 'vehiclepermit' ||
            tripItem.entity_type === 'timedentry' ||
            tripItem.entity_type === 'activitypass' ||
            tripItem.entity_type === 'treepermit'
        ) {
            id = `${tripItem.entity_id}_${tripItem.entity_type}`;
        } else {
            id = `${tripItem.entity_id}_asset`;
        }
    }
    return id;
}

export function getItemDescription(item) {
    let ret = item.description;
    ret = decodeHTML(ret);
    if (ret) {
        ret = ret.substring(0, 175).trim();
        if (!ret.endsWith('.')) {
            ret = `${ret}...`;
        }
    }
    return ret;
}

export function parseSafeDate(date, timeZone = 'UTC') {
    try {
        if (!date) {
            return now(timeZone).toDate().toISOString();
        }

        if (date instanceof Date) {
            return isValid(date)
                ? date.toISOString()
                : now(timeZone).toDate().toISOString();
        }

        if (typeof date === 'string') {
            try {
                // Try parseAbsolute first for ISO format with time
                const parsedAbsolute = parseAbsolute(date, timeZone);
                return parsedAbsolute.toDate().toISOString();
            } catch {
                try {
                    // Try parseDate for date-only formats (YYYY-MM-DD)
                    const parsedDate = parseDate(date);
                    return parsedDate.toDate(timeZone).toISOString();
                } catch {
                    // If all parsing attempts fail, return current date
                    return now(timeZone).toDate().toISOString();
                }
            }
        }

        return now(timeZone).toDate().toISOString();
    } catch {
        return now(timeZone).toDate().toISOString();
    }
}

// get array of trip dates by calculating from start date + sum of all destination stays
export function getTripDateRange(trip) {
    let tripStartDate = now('UTC').toDate().toISOString();
    if (trip.start_date) {
        tripStartDate = trip.start_date;
    }

    const totalDays = trip.destinations.reduce((sum, dest) => {
        if (dest.duration) {
            return sum + parseInt(dest.duration, 10);
        }
        return sum;
    }, 0);

    const tripDateRange = [];
    for (let i = 0; i <= totalDays; i++) {
        tripDateRange.push(addDays(parseSafeDate(tripStartDate), i).toISOString());
    }

    return tripDateRange;
}

export function getTripStartEndDates(trip, defaultToNow) {
    let tripStartDate = null;
    let tripEndDate = null;
    if (defaultToNow || (trip.start_date && trip.start_date !== '')) {
        const tripDateRange = getTripDateRange(trip);
        if (tripDateRange && tripDateRange.length > 1) {
            tripStartDate = tripDateRange[0];
            tripEndDate = tripDateRange[tripDateRange.length - 1];
            if (trip.end_date && trip.end_date !== '') {
                const endDate = trip.end_date;
                if (isAfter(endDate, tripEndDate)) {
                    tripEndDate = endDate;
                }
            }
        }
    }
    return [tripStartDate, tripEndDate];
}

export const isTripItemFeatureInItinerary = (feat, destinations) => {
    const { entityId, entityType } = feat?.properties;
    const found = destinations
        .reduce((items, destination) => {
            return [...items, ...destination.items];
        }, [])
        .find(
            (items) => items.entity_id === entityId && items.entity_type === entityType
        );
    return !!found;
};

export const getSuggestionByKey = (key, suggestions) => {
    if (!key) return null;

    for (let i = 0; i < suggestions.length; i += 1) {
        const suggestion = suggestions[i];

        if (suggestion.suggestions !== undefined) {
            const suggestionOption = suggestion.suggestions.find(
                (option) => option.id === key
            );

            if (suggestionOption) {
                return suggestionOption;
            }
        } else if (suggestion.id === key) {
            return suggestion;
        }
    }

    return null;
};

const checkValue = (value) => {
    if (!value) return '';

    return value;
};

export const addUniqueIdsToSuggestions = (suggestions = [], idKey = 'id') => {
    const nextSuggestions = suggestions.map((suggestion) => ({
        ...suggestion,
        [idKey]: `${checkValue(suggestion.lat)}${checkValue(suggestion.lng)}${checkValue(suggestion.entity_id)}${checkValue(suggestion.text)}`,
    }));

    return nextSuggestions;
};
